import type { Feature } from '@paypalcorp/c3-graphql-schema';
import type ApolloClient from 'apollo-client';
import type { SharedGlobals, GlobalUser, GlobalAuth } from './types';

/**
 * These values represent the `__INITIAL_DATA__` payload provided by the server, and is
 * used by all client apps for hydration. Do not update values here without taking
 * into consideration updating the server supplied payload in InitialData.js.
 *
 * To use this file, import from your client app:
 * `import * as globals from '@paypalcorp/c3-shared-globals';`
 */

const checkWindow = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const check = window;
    return true;
  } catch (e) {
    return false;
  }
};

let globals: Partial<SharedGlobals> = Object.assign(
  {
    correlationId: null,
    fundingSource: null,
    user: {
      ecToken: null,
      email: null,
      firstName: null,
      lastName: null,
      auth: {
        state: null,
        type: null,
        access_token: null,
        claims: [],
      },
    },
    locality: {
      country: 'US',
      language: 'en',
      directionality: 'ltr',
    },
    geo: {
      // point to these urls by default
      domain: 'paypal.com',
      cdnHostName: 'www.paypalobjects.com',
      stagingHostName: 'msmaster.qa.paypal.com',
    },
    uri: {
      queryEndpoint: null,
      loggerEndpoint: null,
      fallbackEndpoint: null,
      authchallengeEndpoint: null,
    },
    useraction: null,
    fallback: {
      endpoint: null,
      redirectOnError: null,
    },
    isInternalRequest: null,
    widgetBundleUrls: {
      cap: {
        capBaseUri: 'https://www.paypalobjects.com/cap-assets/', // Point to prod by default
      },
    },
    deployEnv: {},
    features: {},
    experiments: {},
    isLinkAndPay: false,
    isScaRegion: false,
    mockedMode: false,
    northstar: false,
    redirectedToAuth: false,
    commonWebFlows: {
      returnParams: {},
    },
    cookielessQueryParams: null,
    credit: {
      productIdentifier: null,
      offerToken: null,
      newUser: false,
      isCreditSecondButton: false,
    },
    isV3: false,
  },
  checkWindow() ? window.__INITIAL_DATA__ || {} : {}
);

export const initGlobals = () => {
  // remove initial data in production from the global window object / DOM
  // so initial data is not easily accessible, especially such stuff as access_token and other user info
  // Have to use `parentNode.removeChild` for IE11 compatibility.
  if (process.env.NODE_ENV === 'production') {
    delete window.__INITIAL_DATA__;
    const dataScript = document.getElementById('initial-data-script');
    if (dataScript && dataScript.parentNode) {
      dataScript.parentNode.removeChild(dataScript);
    }
  }
  return globals;
};

export const updateGlobals = (updatedGlobals: Partial<SharedGlobals>): Partial<SharedGlobals> => {
  if (updatedGlobals) {
    globals = Object.assign(globals, updatedGlobals);
  }
  return globals;
};

export const isTesting: boolean = process.env.NODE_ENV?.toLowerCase() === 'test';
export const getGlobals = () => globals;
export const getWidgetBundleUrls = () => globals.widgetBundleUrls || {};
export const getUser = (): Partial<GlobalUser> => globals.user || {};
export const getUserFullName = (): string => `${getUser().firstName} ${getUser().lastName}`;
export const getToken = (): string | undefined | null => getUser()?.ecToken;
export const getAuth = (): GlobalAuth | undefined => getUser()?.auth || {};
export const getAccessToken = () => getAuth()?.access_token || '';
export const getAuthState = () => getAuth()?.state;
export const getClaims = () => getAuth()?.claims || [];
export const getCountry = () => globals.locality?.country;
export const getLocale = () => globals.locality?.language;
export const getDirectionality = () => globals.locality?.directionality;
export const getFallback = () => globals.fallback || ({} as Record<string, never>);
export const getUserAction = () => (window?.xprops?.commit ? 'commit' : globals.useraction);
export const getFeatures = () => globals.features;
export const getExperiments = (): Record<string, Record<string, unknown>> =>
  globals.experiments ?? {}; // get elmo experiments from init data
export const getIsInternalRequest = () => getGlobals().isInternalRequest || isTesting;
export const getIsLinkAndPay = () => globals.isLinkAndPay;
export const getIsScaRegion = () => globals.isScaRegion;
export const getIsMockedMode = () => globals.mockedMode;
export const getIsNorthstar = () => globals.northstar;
export const getCredit = () => globals.credit;
export const getFundingSource = () => globals.fundingSource;
export const getCommonWebFlows = () => globals.commonWebFlows;
export const getQueryParams = () => globals.queryParams;
export const getCookielessQueryParams = () => {
  return globals.cookielessQueryParams;
};
export const getIsV3Global = () => globals.isV3;

let _client: ApolloClient<any>;

export function getClient(): ApolloClient<any> {
  if (!_client) {
    throw new Error('ApolloClient not initialized');
  }
  return _client;
}

export function setClient(client: ApolloClient<any>): void {
  _client = client;
}

/**
 * Return if a feature exists in the feature flag list
 * @param feature the feature flag
 * @returns true if feature flag exists in list
 */
export const hasFeature = (feature: string): Feature | undefined => globals.features?.[feature];

export const getServerGqlData = (): any => globals.serverGqlData;
export const setServerGqlData = (serverGqlData: unknown) => updateGlobals({ serverGqlData });
export const getPayPalDomain = () => globals.geo?.domain;
export const getCdnHostName = () => globals.geo?.cdnHostName;
export const getStagingHostName = () => globals.geo?.stagingHostName;
